import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Input, MenuProps, theme } from 'antd';
import { faUserCircle } from '@fortawesome/free-regular-svg-icons';
import {
  faCaretDown,
  faChartBar,
  faGear,
  faMagnifyingGlass,
  faPowerOff,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { unsetAdvertiser } from '../../store/actions/globalActions';
import { logout, virtualLogin } from '../../store/actions/authActions';
import { FaIcon } from '../../components';
import useApi from '../../api';

const { useToken } = theme;

type NavbarProps = {
  mobileMenuTrigger?: React.ReactNode;
};

function Navbar({ mobileMenuTrigger }: NavbarProps) {
  const [advertisers, setAdvertisers] = useState<any[]>([]);
  const [advertiserSearch, setAdvertiserSearch] = useState('');

  const [listAdvertiser] = useApi.Advertiser.List();
  const [virtualLoginApi] = useApi.Auth.VirtualLogin();
  const { token } = useToken();

  const dispatch = useDispatch<any>();

  const { userName, userType, advertiserId, userRoles } = useSelector((state: any) => ({
    userName: state.auth.userName,
    userType: state.auth.userType,
    advertiserId: state.auth.advertiserId,
    userRoles: state.auth.userRoles,
  }));

  useEffect(() => {
    if (advertiserId && !advertisers.length) {
      let ids: string[] = [];
      if (userType !== 'ENTERPRISE') ids = userRoles.map((role: any) => role.advertiserId);

      const limit = 100;
      const baseParams = { status: 'ACTIVE', ...(ids?.length && { ids }) };

      const fetchAllAdvertisers = async () => {
        const all: any[] = [];
        let page = 1;
        for (;;) {
          const { data } = await listAdvertiser({ params: { ...baseParams, page, limit } });
          const items = data?.items ?? [];
          all.push(...items);
          const total = data?.totalCount ?? all.length;
          if (items.length < limit || all.length >= total) break;
          page += 1;
        }
        return all;
      };

      fetchAllAdvertisers()
        .then((items) => {
          if (items.length)
            setAdvertisers(items.map((adv: any) => ({ id: adv.id, name: adv.name })));
        })
        .catch(() => {});
    }
  }, [advertiserId, advertisers, listAdvertiser, userRoles, userType]);

  const navigate = useNavigate();

  const performLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const getPerformanceConsole = useCallback(
    (id?: string) => {
      dispatch(
        virtualLogin(
          {
            thenCb: () => navigate('/campaigns'),
          },
          virtualLoginApi,
          id,
        ),
      );
    },
    [dispatch, navigate, virtualLoginApi],
  );

  const getAdminConsole = useCallback(() => {
    dispatch(unsetAdvertiser({ thenCb: () => navigate('/advertisers') }));
  }, [dispatch, navigate]);

  const getAdvertiserName = useCallback(() => {
    const a = advertisers.find((adv) => adv.id === advertiserId);
    return a.name;
  }, [advertiserId, advertisers]);

  const items: MenuProps['items'] = useMemo(() => {
    const menuItems = [
      {
        key: 'logout',
        label: 'Logout',
        icon: <FaIcon icon={faPowerOff} size="lg" />,
        onClick: performLogout,
      },
    ];
    if (userType === 'ENTERPRISE')
      menuItems.unshift(
        ...(!advertiserId
          ? [
            {
              key: 'performance',
              label: 'Performance Console',
              icon: <FaIcon icon={faChartBar} size="lg" />,
              onClick: () => getPerformanceConsole(),
            },
          ]
          : [
            {
              key: 'admin',
              label: 'Admin Console',
              icon: <FaIcon icon={faGear} size="lg" />,
              onClick: getAdminConsole,
            },
          ]),
      );
    return menuItems;
  }, [advertiserId, getAdminConsole, getPerformanceConsole, performLogout, userType]);

  const advertiserItems: MenuProps['items'] = useMemo(() => {
    const query = advertiserSearch.trim().toLowerCase();
    return advertisers
      .filter((adv) => adv.name.toLowerCase().includes(query))
      .map((adv) => ({
        key: adv.id,
        label: adv.name,
        onClick: () => getPerformanceConsole(adv.id),
      }));
  }, [advertisers, advertiserSearch, getPerformanceConsole]);

  const getPopupContainer = useCallback(() => {
    const el = document.querySelector<any>('.navbar');
    if (el) el.style.backgroundColor = token.colorBgContainer;
    return el;
  }, [token.colorBgContainer]);

  return (
    <>
      <div className="navbar-left">{mobileMenuTrigger}</div>
      {advertiserId && advertisers.length ? (
        <Dropdown
          menu={{
            items: advertiserItems,
            style: { maxHeight: 260, overflowY: 'auto', boxShadow: 'none' },
          }}
          placement="bottom"
          trigger={['click']}
          getPopupContainer={getPopupContainer}
          onOpenChange={(open) => {
            if (!open) setAdvertiserSearch('');
          }}
          dropdownRender={(menu) => (
            <div
              className="advertiser-dropdown"
              style={{
                backgroundColor: token.colorBgElevated,
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
              }}
            >
              <div
                className="advertiser-dropdown-search"
                onClick={(e) => e.stopPropagation()}
                role="presentation"
              >
                <Input
                  autoFocus
                  allowClear
                  placeholder="Search"
                  value={advertiserSearch}
                  onChange={(e) => setAdvertiserSearch(e.target.value)}
                  suffix={<FaIcon icon={faMagnifyingGlass} />}
                />
              </div>
              {menu}
            </div>
          )}
        >
          <div className="navbar-advertiser">
            <span>{getAdvertiserName()}</span>
            <FaIcon icon={faCaretDown} />
          </div>
        </Dropdown>
      ) : null}
      <Dropdown
        menu={{ items }}
        placement="bottomRight"
        overlayClassName="navbar-overlay"
        trigger={['click']}
        getPopupContainer={getPopupContainer}
      >
        <div>
          <FaIcon icon={faUserCircle} size="lg" />
          <span>{userName}</span>
          <FaIcon icon={faCaretDown} />
        </div>
      </Dropdown>
    </>
  );
}

export default Navbar;
